/** Deal Inbox page (v1 deal-inbox.js dealInboxPanel). Setup status (read),
 * Gmail connect (OAuth — opens the server flow), scan-now, paste-to-parse, and
 * the Facebook deal drop (text + screenshot). Every lead-creating action is
 * gated behind liveFire `dealInbox` (ships OFF) so a stray click on beta can't
 * mint real leads before the supervised enable + Gmail connect. */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchInboxStatus, parseText, parseImage, scanGmail, gmailConnectUrl,
  type InboxStatus, type ParseResult, type ScanResult,
} from "./api";

type Note = { text: string; err: boolean } | null;

const errText = (e: unknown): string => (e instanceof Error ? e.message : "Backend unreachable");

function parseNote(r: ParseResult): Note {
  if (r.created) return { text: `Lead created: ${r.address} ✓`, err: false };
  if (r.reason === "duplicate") return { text: `Already have that address${r.address ? ": " + r.address : ""}`, err: true };
  if (r.reason === "no_text_in_image") return { text: "Couldn't read any text in that screenshot", err: true };
  return { text: "That didn't look like a deal — no lead created", err: true };
}

export function DealInboxPage({
  loadStatus, onParseText, onParseImage, onScan, canFire, autoScan, onToggleAutoScan,
}: {
  loadStatus: () => Promise<InboxStatus>;
  onParseText: (body: string, source: "Manual" | "Facebook", sender?: string) => Promise<ParseResult>;
  onParseImage: (b64: string, mime: string, poster?: string) => Promise<ParseResult>;
  onScan: () => Promise<ScanResult>;
  canFire: boolean;
  autoScan: boolean;
  onToggleAutoScan: (on: boolean) => void;
}) {
  const [status, setStatus] = useState<InboxStatus | null>(null);
  const [statusErr, setStatusErr] = useState("");
  const [paste, setPaste] = useState("");
  const [fbText, setFbText] = useState("");
  const [poster, setPoster] = useState("");
  const [busy, setBusy] = useState("");
  const [pasteNote, setPasteNote] = useState<Note>(null);
  const [fbNote, setFbNote] = useState<Note>(null);
  const [scanNote, setScanNote] = useState<Note>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const refreshStatus = useCallback(() => {
    setStatusErr("");
    void loadStatus().then(setStatus).catch((e) => setStatusErr(errText(e)));
  }, [loadStatus]);
  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const doPaste = async () => {
    if (!canFire || !paste.trim()) return;
    setBusy("paste"); setPasteNote(null);
    try { setPasteNote(parseNote(await onParseText(paste, "Manual"))); if (paste) setPaste(""); }
    catch (e) { setPasteNote({ text: "Parse failed: " + errText(e), err: true }); }
    finally { setBusy(""); }
  };
  const doFbText = async () => {
    if (!canFire || !fbText.trim()) return;
    setBusy("fb"); setFbNote(null);
    try { const r = await onParseText(fbText, "Facebook", poster); setFbNote(parseNote(r)); if (r.created) setFbText(""); }
    catch (e) { setFbNote({ text: "Parse failed: " + errText(e), err: true }); }
    finally { setBusy(""); }
  };
  const doImage = (file: File) => {
    if (!canFire) return;
    if (file.size > 6 * 1024 * 1024) { setFbNote({ text: "Screenshot too big (max ~6MB) — crop to the post", err: true }); return; }
    setBusy("fb"); setFbNote({ text: "Reading screenshot…", err: false });
    const rd = new FileReader();
    rd.onload = async () => {
      try { setFbNote(parseNote(await onParseImage(String(rd.result), file.type || "image/jpeg", poster))); }
      catch (e) { setFbNote({ text: "Screenshot parse failed: " + errText(e), err: true }); }
      finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
    };
    rd.readAsDataURL(file);
  };
  const doScan = async () => {
    if (!canFire) return;
    setBusy("scan"); setScanNote({ text: "Scanning email for deals…", err: false });
    try {
      const r = await onScan();
      const parts = [r.createdCount ? `Created ${r.createdCount} new lead${r.createdCount > 1 ? "s" : ""} ✓` : `Scanned ${r.scanned} — no new deals`];
      if (r.priceDropCount) parts.push(`${r.priceDropCount} price drop${r.priceDropCount > 1 ? "s" : ""}`);
      setScanNote({ text: parts.join(" · "), err: false });
    } catch (e) { setScanNote({ text: "Scan failed: " + errText(e), err: true }); }
    finally { setBusy(""); }
  };

  // v1 inboxRestartAuto: when "Auto-scan every 5 min" is on (and scanning is
  // live-fire enabled), run a real 5-minute interval. A ref keeps the latest
  // doScan without re-arming the timer each render.
  const doScanRef = useRef(doScan);
  doScanRef.current = doScan;
  useEffect(() => {
    if (!autoScan || !canFire) return;
    const id = setInterval(() => { void doScanRef.current(); }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [autoScan, canFire]);

  const noteEl = (n: Note) => (n ? <div className={"inbxnote" + (n.err ? " err" : "")}>{n.text}</div> : null);
  const gate = !canFire ? <span className="inbxgate">Turn on in the go-live sit-down — creates real leads</span> : null;

  return (
    <div className="leads exppage">
      <div className="anlhero">
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <span style={{ width: 44, height: 44, flex: "none", borderRadius: 12, background: "#4f46e5", display: "grid", placeItems: "center", fontSize: 20 }}>📥</span>
          <div className="anlherotext">
            <h1>Deal Inbox</h1>
            <p>Auto-turn deal emails, texts &amp; Facebook posts into new leads with AI — it stays gated until you enable it together.</p>
          </div>
        </div>
        <div className="expacts"><button className="btn slim" onClick={refreshStatus}>↻ Refresh</button></div>
      </div>

      <div className="inbxcard">
        <h3>Setup</h3>
        {statusErr ? <div className="inbxnote err">Backend: {statusErr}</div> : null}
        {status === null && !statusErr ? <div className="inbxrow"><span>Checking setup…</span></div> : null}
        {status ? (
          <>
            <div className="inbxrow"><span>AI parser (OpenAI / Anthropic key)</span><span className={"inbxpill " + (status.aiConfigured ? "on" : "off")}>{status.aiConfigured ? "Ready" : "Not set"}</span></div>
            <div className="inbxrow"><span>Deal-email Gmail connected</span><span className={"inbxpill " + (status.gmailConnected && status.gmailScope ? "on" : "off")}>{status.gmailConnected && status.gmailScope ? "Connected" : "Not connected"}</span></div>
            <div className="inbxrow"><span>Scanning senders</span><span className="inbxsub">{status.senders.length ? status.senders.join(", ") : "all recent unread"}</span></div>
            <div className="inbxconnect">
              <span>Connect the Gmail that <b>receives your deals</b> (separate from Drive).</span>
              <a className="btn slim primary" href={gmailConnectUrl()} target="_blank" rel="noreferrer">{status.gmailConnected ? "Reconnect Gmail" : "Connect deal-email Gmail"}</a>
            </div>
            {!status.gmailOauthConfigured ? <div className="inbxnote err">Gmail sign-in isn't configured on the server yet (INBOX_GOOGLE_CLIENT_ID / _SECRET).</div> : null}
            {!status.aiConfigured ? <div className="inbxnote err">No AI key set on the server (OPENAI_API_KEY) — parsing won't work until it's added.</div> : null}
          </>
        ) : null}
      </div>

      <div className="inbxcard">
        <h3>Scan email for deals</h3>
        <p className="inbxp">Pulls recent deal emails from your connected Gmail, parses each with AI, and creates a lead for every real deal (address duplicates are skipped).</p>
        <div className="inbxactrow">
          <button className="btn slim primary" onClick={doScan} disabled={!canFire || busy === "scan"}>{busy === "scan" ? "Scanning…" : "Scan email now"}</button>
          <label className="inbxtoggle"><input type="checkbox" checked={autoScan} onChange={(e) => onToggleAutoScan(e.target.checked)} /> Auto-scan every 5 min while open</label>
          {gate}
        </div>
        {noteEl(scanNote)}
      </div>

      <div className="inbxcard">
        <h3>Paste a deal manually</h3>
        <p className="inbxp">Paste the body of any deal email or text. AI extracts the property + seller and creates a lead.</p>
        <textarea className="inbxta" rows={4} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="e.g. Off-market in Tampa — 123 Main St, 3/2, 1540 sqft, asking $215k, ARV ~$310k, needs ~$40k. Call Mike 407-555-1234" />
        <div className="inbxactrow">
          <button className="btn slim primary" onClick={doPaste} disabled={!canFire || busy === "paste" || !paste.trim()}>{busy === "paste" ? "Parsing…" : "Parse & create lead"}</button>
          {gate}
        </div>
        {noteEl(pasteNote)}
      </div>

      <div className="inbxcard">
        <h3>Facebook Deal Drop</h3>
        <p className="inbxp">For your VA: paste a group post's text or upload a screenshot. AI reads it, creates the lead, and adds the poster to Contacts as a wholesaler.</p>
        <textarea className="inbxta" rows={3} value={fbText} onChange={(e) => setFbText(e.target.value)} placeholder="Paste the Facebook post text here… (address, asking, ARV, poster's phone — whatever the post says)" />
        <div className="inbxactrow">
          <input className="inbxposter" value={poster} onChange={(e) => setPoster(e.target.value)} placeholder="Poster name (optional)" />
          <button className="btn slim primary" onClick={doFbText} disabled={!canFire || busy === "fb" || !fbText.trim()}>{busy === "fb" ? "Parsing…" : "Parse post"}</button>
          <label className={"btn slim inbxupload" + (!canFire ? " disabled" : "")}>
            Upload screenshot
            <input ref={fileRef} type="file" accept="image/*" disabled={!canFire || busy === "fb"} onChange={(e) => { const f = e.target.files?.[0]; if (f) doImage(f); }} hidden />
          </label>
          {gate}
        </div>
        {noteEl(fbNote)}
      </div>
    </div>
  );
}
